// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Switch } from "components/form/elements";
import { ALL_MODERATORS, RestrictedArea, ROLES } from "containers/Permissions";
import { useActiveOrganization } from "hooks/utils";
import { useManifestsFilters } from "src/hooks/manifests";
import { trans } from "src/translations";
import { parseLocationToLimitOffset } from "utils/pagination";
import {
  addQueryParamsToLocation,
  createLocationWithSearch,
  createSortingQueryParams,
} from "utils/url";

import { ITEMS_PER_PAGE } from "./constants";
import { useAppsWithProviders } from "./hooks/useAppsWithProviders";
import { useManifestFiltersSuggestions } from "./hooks/useManifestFiltersSuggestions";
import { useManifestsGroups } from "./hooks/useManifestsGroups";
import styles from "./ManifestList.scss";
import { ManifestsListRend } from "./ManifestListRend";
import {
  parseManifestFilters,
  retrieveFiltersFromSearchBar,
  getSearchParams,
} from "./utils";

const SHOW_DELETED_KEY = "show_deleted";

export function ManifestListData() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeOrganization } = useActiveOrganization();
  const userRoles = activeOrganization?.roles ?? [];

  const showDeleted = useMemo(
    () => getSearchParams(location)[SHOW_DELETED_KEY] === "true",
    [location]
  );
  const isAppIntegrator = userRoles.includes(ROLES.moderator.appIntegrator);

  const getQueryParams = useCallback(() => {
    const searchParams = getSearchParams(location);
    const filters = retrieveFiltersFromSearchBar(searchParams);
    const pagination = parseLocationToLimitOffset(location, ITEMS_PER_PAGE);
    const sorting = createSortingQueryParams(
      searchParams.sort_by,
      searchParams.order
    );

    return {
      ...filters,
      ...pagination,
      ...sorting,
    };
  }, [location]);

  const { data: manifestsByProvider, ...helpers } =
    useManifestsGroups(getQueryParams());
  const { data: filters } = useManifestsFilters();
  const { fetchSuggestions } = useManifestFiltersSuggestions();

  const providerIds = useMemo(
    () =>
      (manifestsByProvider?.results || []).map(({ providerId }) => providerId),
    [manifestsByProvider?.results]
  );

  const { apps, ...appsHelpers } = useAppsWithProviders(
    providerIds,
    !helpers.isPendinguseManifestsGroups
  );

  const availableFilters = useMemo(
    () => filters?.results && parseManifestFilters(filters.results),
    [filters?.results]
  );

  const toggleShowDeleted = () => {
    const newLocation = addQueryParamsToLocation(location, {
      [SHOW_DELETED_KEY]: !showDeleted,
    });
    navigate(newLocation);
  };

  const handleSearch = (filters) => {
    const updatedLocation = createLocationWithSearch(location, filters);
    navigate(updatedLocation);
  };

  return (
    <RestrictedArea
      allowed={[...ALL_MODERATORS, ROLES.tivo.preview]}
      showFallback={true}
    >
      <ManifestsListRend
        manifestsByProvider={manifestsByProvider?.results}
        apps={apps}
        count={manifestsByProvider?.meta?.total}
        loading={helpers.isPending || appsHelpers.isPending}
        error={helpers.error}
        availableFilters={availableFilters}
        fetchSuggestions={fetchSuggestions}
        onSearch={handleSearch}
        isAppIntegrator={isAppIntegrator}
        defaultOrder={{ sortBy: "modified_date", sortOrder: "DESC" }}
        onRefetch={helpers.refetch}
        toggleButton={
          <Switch
            label={trans.APP_MANIFESTS_SHOW_DELETED_LABEL()}
            dataTestId="manifests-show-deleted-toggle"
            onChange={toggleShowDeleted}
            checked={showDeleted}
            className={styles.showDeleted}
          />
        }
      />
    </RestrictedArea>
  );
}
